import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Plus, Sparkles, TrendingUp, Trash2, Clock, CheckCircle,
  AlertCircle, Loader, Calendar, Check, CreditCard
} from "lucide-react";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { key: "pending", icon: Clock, color: "#6b6b6b", bg: "rgba(100,100,100,0.1)" },
  generating: { key: "generating", icon: Loader, color: "#c084fc", bg: "rgba(168,85,247,0.1)" },
  generating_images: { key: "generating_images", icon: Loader, color: "#c084fc", bg: "rgba(168,85,247,0.1)" },
  text_ready: { key: "text_ready", icon: CheckCircle, color: "#86efac", bg: "rgba(134,239,172,0.1)" },
  images_ready: { key: "images_ready", icon: CheckCircle, color: "#c084fc", bg: "rgba(168,85,247,0.15)" },
  failed: { key: "failed", icon: AlertCircle, color: "#f87171", bg: "rgba(248,113,113,0.1)" },
};

const PLATFORM_COLORS = {
  "TikTok": "#ff4757",
  "Instagram Reels": "#e1306c",
  "YouTube Shorts": "#ff0000",
  "Snapchat": "#fffc00",
  "Facebook": "#1877f2",
  "Twitter / X": "#e7e9ea",
};

function getCountdown(scheduledAt, t) {
  const diff = new Date(scheduledAt) - new Date();
  if (diff < 0) return t("dashboard.scheduled.overdue");
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return t("dashboard.scheduled.inDaysHours", { days: d, hours: h % 24 });
  if (h > 0) return t("notifications.inHours", { count: h });
  return t("dashboard.scheduled.inLessThanHour");
}

function ScheduledPostCard({ post, onMarkPosted, onDelete }) {
  const { t, i18n } = useTranslation();
  const isPosted = post.status === "posted";
  const isOverdue = !isPosted && new Date(post.scheduled_at) < new Date();
  const color = PLATFORM_COLORS[post.platform] || "#c084fc";

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4"
      style={{ background: "#1c1c1c", border: `1px solid ${isPosted ? "rgba(134,239,172,0.2)" : isOverdue ? "rgba(248,113,113,0.2)" : "#2a2a2a"}` }}
      data-testid={`scheduled-post-${post.schedule_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
          <span className="text-sm font-bold text-white">{post.platform}</span>
          {isPosted && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(134,239,172,0.1)", color: "#86efac" }}>{t("dashboard.scheduled.posted")}</span>}
          {isOverdue && !isPosted && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(248,113,113,0.1)", color: "#f87171" }}>{t("dashboard.scheduled.overdue")}</span>}
        </div>
        <button onClick={() => onDelete(post.schedule_id)}
          data-testid={`delete-scheduled-${post.schedule_id}`}
          className="p-1 rounded-lg hover:text-red-400 transition-colors" style={{ color: "#6b6b6b" }}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <p className="text-xs mb-1" style={{ color: "#6b6b6b" }}>{post.business_name}</p>
      {post.caption && (
        <p className="text-xs leading-relaxed mb-2 line-clamp-2" style={{ color: "#6b6b6b" }}>{post.caption}</p>
      )}

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3" style={{ color: "#6b6b6b" }} />
          <span className="text-xs" style={{ color: "#6b6b6b" }}>
            {new Date(post.scheduled_at).toLocaleDateString(i18n.language, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
          {!isPosted && (
            <span className="text-xs font-medium" style={{ color: isOverdue ? "#f87171" : "#c084fc" }}>
              {getCountdown(post.scheduled_at, t)}
            </span>
          )}
        </div>
        {!isPosted && (
          <button onClick={() => onMarkPosted(post.schedule_id)}
            data-testid={`mark-posted-${post.schedule_id}`}
            className="flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium transition-all"
            style={{ background: "rgba(134,239,172,0.08)", border: "1px solid rgba(134,239,172,0.2)", color: "#86efac" }}>
            <Check className="w-3 h-3" /> {t("dashboard.scheduled.markPosted")}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function ProjectCard({ project, onDelete }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const status = statusConfig[project.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 cursor-pointer card-hover group transition-all"
      style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
      onClick={() => navigate(project.status === "pending" ? `/projects/new?draft=${project.project_id}` : `/projects/${project.project_id}`)}
      data-testid={`project-card-${project.project_id}`}
      onMouseOver={e => e.currentTarget.style.borderColor = "rgba(168,85,247,0.3)"}
      onMouseOut={e => e.currentTarget.style.borderColor = "#2a2a2a"}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white truncate text-base" style={{ fontFamily: "Epilogue, sans-serif" }}>{project.business_name}</h3>
          <p className="text-xs mt-0.5 truncate" style={{ color: "#6b6b6b" }}>{project.industry}</p>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete(project.project_id); }} data-testid={`delete-project-${project.project_id}`}
          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg transition-all ml-2 flex-shrink-0 hover:text-red-400"
          style={{ color: "#6b6b6b" }}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <p className="text-xs leading-relaxed mb-4 line-clamp-2" style={{ color: "#6b6b6b" }}>{project.description}</p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {project.platforms?.map(p => (
          <span key={p} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "#252525", color: "#a0a0a0", border: "1px solid #333" }}>{p}</span>
        ))}
        {project.tone && (
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(168,85,247,0.1)", color: "#c084fc", border: "1px solid rgba(168,85,247,0.2)" }}>{project.tone}</span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: status.bg, color: status.color }}>
          <StatusIcon className={`w-3.5 h-3.5 ${["generating","generating_images"].includes(project.status) ? "animate-spin" : ""}`} />
          {t(`dashboard.status.${status.key}`)}
        </div>
        {project.variants?.length > 0 && (
          <span className="text-xs" style={{ color: "#6b6b6b" }}>{t("dashboard.variants", { count: project.variants.length })}</span>
        )}
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("projects");
  const [usage, setUsage] = useState({ text_generations: 0, image_generations: 0, voiceover_generations: 0 });
  const [credits, setCredits] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [projRes, usageRes, schedRes, billingRes] = await Promise.all([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/usage`, { withCredentials: true }),
        axios.get(`${API}/schedule`, { withCredentials: true }),
        axios.get(`${API}/billing/credits`, { withCredentials: true }),
      ]);
      setProjects(projRes.data || []);
      setUsage(usageRes.data || {});
      setScheduledPosts(schedRes.data || []);
      setCredits(billingRes.data?.credits ?? null);
      setSubscriptionStatus(billingRes.data?.subscription_status ?? null);
    } catch (err) {
      console.error("Dashboard initial load failed:", err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (projectId) => {
    if (!window.confirm(t("dashboard.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      setProjects(projects.filter(p => p.project_id !== projectId));
      toast.success(t("dashboard.toasts.deleted"));
    } catch { toast.error(t("dashboard.toasts.deleteFailed")); }
  };

  const handleMarkPosted = async (scheduleId) => {
    try {
      await axios.patch(`${API}/schedule/${scheduleId}/mark-posted`, {}, { withCredentials: true });
      setScheduledPosts(prev => prev.map(p => p.schedule_id === scheduleId ? { ...p, status: "posted" } : p));
      toast.success(t("dashboard.toasts.markedPosted"));
    } catch { toast.error(t("dashboard.toasts.updateFailed")); }
  };

  const handleDeleteScheduled = async (scheduleId) => {
    try {
      await axios.delete(`${API}/schedule/${scheduleId}`, { withCredentials: true });
      setScheduledPosts(prev => prev.filter(p => p.schedule_id !== scheduleId));
      toast.success(t("dashboard.toasts.removedSchedule"));
    } catch { toast.error(t("dashboard.toasts.deleteFailed")); }
  };

  const pendingPosts = scheduledPosts.filter(p => p.status !== "posted");

  const stats = [
    { label: t("dashboard.stats.totalProjects"), value: projects.length, icon: TrendingUp },
    { label: t("dashboard.stats.adsGenerated"), value: projects.reduce((a, p) => a + (p.variants?.length || 0), 0), icon: Sparkles },
    { label: t("dashboard.stats.scheduledPosts"), value: pendingPosts.length, icon: Calendar },
  ];

  return (
    <Layout>
      {/* Subscription paywall — required to generate */}
      {subscriptionStatus !== "active" && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between gap-4 rounded-2xl px-5 py-4 mb-6"
          style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.3)" }}
          data-testid="subscription-paywall-banner">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 flex-shrink-0" style={{ color: "#c084fc" }} />
            <div>
              <p className="text-sm font-bold text-white">
                {subscriptionStatus === "canceled" ? t("dashboard.paywall.canceledTitle") : t("dashboard.paywall.requiredTitle")}
              </p>
              <p className="text-xs mt-0.5" style={{ color: "#a0a0a0" }}>
                {subscriptionStatus === "canceled"
                  ? t("dashboard.paywall.canceledBody", { credits: credits ?? 0 })
                  : t("dashboard.paywall.requiredBody")}
              </p>
            </div>
          </div>
          <Link to="/billing" data-testid="paywall-cta-btn"
            className="flex-shrink-0 text-xs font-bold px-4 py-2 rounded-full transition-colors"
            style={{ background: "#7c3aed", color: "white" }}>
            {subscriptionStatus === "canceled" ? t("billing.plans.resubscribe") : t("dashboard.paywall.choosePlan")}
          </Link>
        </motion.div>
      )}

      {/* Low-credit warning banner */}
      {credits !== null && credits < 10 && subscriptionStatus === "active" && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between gap-4 rounded-2xl px-5 py-3 mb-6"
          style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.25)" }}
          data-testid="low-credit-banner">
          <div className="flex items-center gap-3">
            <CreditCard className="w-4 h-4 flex-shrink-0" style={{ color: "#fb923c" }} />
            <p className="text-sm" style={{ color: "#fdba74" }}>
              {credits === 0 ? t("dashboard.lowCredit.none") : t("dashboard.lowCredit.some", { count: credits })}
            </p>
          </div>
          <Link to="/billing" data-testid="low-credit-buy-btn"
            className="flex-shrink-0 text-xs font-bold px-4 py-1.5 rounded-full transition-colors"
            style={{ background: "rgba(251,146,60,0.15)", color: "#fb923c", border: "1px solid rgba(251,146,60,0.3)" }}>
            {t("dashboard.lowCredit.buy")}
          </Link>
        </motion.div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("dashboard.title")}</h1>
          <p className="text-sm mt-1" style={{ color: "#6b6b6b" }}>
            {projects.length === 0 ? t("dashboard.subtitleEmpty") : t("dashboard.subtitleCount", { count: projects.length })}
          </p>
        </div>
        <button onClick={() => navigate("/projects/new")} data-testid="dashboard-create-btn"
          className="ghost-purple text-sm px-5 py-2.5 rounded-full font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("nav.newAd")}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {stats.map(stat => (
          <div key={stat.label} className="rounded-2xl p-4 flex items-center gap-4" style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.15)" }}>
              <stat.icon className="w-5 h-5" style={{ color: "#c084fc" }} />
            </div>
            <div>
              <div className="text-2xl font-black text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{stat.value}</div>
              <div className="text-xs" style={{ color: "#6b6b6b" }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl w-fit" style={{ background: "#161616", border: "1px solid #252525" }}>
        {[
          { id: "projects",  label: t("dashboard.tabs.projects"),  count: projects.length },
          { id: "scheduled", label: t("dashboard.tabs.scheduled"), count: pendingPosts.length },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={{
              background: activeTab === tab.id ? "#252525" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#6b6b6b"
            }}>
            {tab.label}
            {tab.count > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                style={{ background: activeTab === tab.id ? "rgba(168,85,247,0.2)" : "#252525", color: activeTab === tab.id ? "#c084fc" : "#6b6b6b" }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overdue banner */}
      {activeTab === "scheduled" && scheduledPosts.some(p => p.status !== "posted" && new Date(p.scheduled_at) < new Date()) && (
        <div className="flex items-center gap-3 rounded-xl px-4 py-3 mb-5"
          style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)" }}
          data-testid="overdue-banner">
          <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#f87171" }} />
          <p className="text-sm" style={{ color: "#f87171" }}>
            {t("dashboard.scheduled.overdueBanner")}
          </p>
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === "projects" && (
        loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl p-5 animate-pulse" style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
                <div className="h-4 rounded w-3/4 mb-3" style={{ background: "#252525" }} />
                <div className="h-3 rounded w-1/2 mb-4" style={{ background: "#252525" }} />
                <div className="h-8 rounded mb-3" style={{ background: "#252525" }} />
                <div className="h-5 rounded w-1/3" style={{ background: "#252525" }} />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6" style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.15)" }}>
              <Sparkles className="w-10 h-10" style={{ color: "#c084fc" }} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("dashboard.empty.title")}</h3>
            <p className="text-sm max-w-sm mb-6" style={{ color: "#6b6b6b" }}>{t("dashboard.empty.body")}</p>
            <button onClick={() => navigate("/projects/new")} data-testid="empty-create-btn"
              className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-8 py-3 font-bold transition-colors flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("dashboard.empty.cta")}
            </button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map(project => (
              <ProjectCard key={project.project_id} project={project} onDelete={handleDelete} />
            ))}
          </div>
        )
      )}

      {/* Scheduled Tab */}
      {activeTab === "scheduled" && (
        scheduledPosts.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6" style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.15)" }}>
              <Calendar className="w-10 h-10" style={{ color: "#c084fc" }} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("dashboard.scheduled.emptyTitle")}</h3>
            <p className="text-sm max-w-sm" style={{ color: "#6b6b6b" }}>{t("dashboard.scheduled.emptyBody")}</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scheduledPosts.map(post => (
              <ScheduledPostCard key={post.schedule_id} post={post}
                onMarkPosted={handleMarkPosted} onDelete={handleDeleteScheduled} />
            ))}
          </div>
        )
      )}
    </Layout>
  );
}
